import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useShopContext } from "../app/ShopGuard";
import { CategoryChips } from "../features/catalog/CategoryChips";
import { ProductGrid } from "../features/catalog/ProductGrid";
import { SearchBar } from "../features/catalog/SearchBar";
import { useCategories } from "../features/catalog/useCategories";
import { useCart } from "../features/cart/useCart";
import { useDebouncedValue } from "../hooks/useDebouncedValue";
import { messages } from "../lib/messages";
import { mainButton } from "../lib/telegram";

const SEARCH_MIN_LENGTH = 2;
const SEARCH_DEBOUNCE_MS = 300;

/**
 * CatalogPage — главный экран витрины (docs/07-miniapp-ux.md §2.2): шапка магазина,
 * поиск, чипсы категорий, сетка товаров. В шапке — кнопка заказов с подписью и кнопка
 * корзины со значком количества; MainButton внизу показывает сумму («Корзина · 2 490 ₽»).
 *
 * Кнопка корзины видна и при пустой корзине: MainButton тогда скрыт, и другого пути
 * в корзину у покупателя нет. Значок с количеством появляется, когда есть что считать.
 */
export function CatalogPage() {
  const { shop } = useShopContext();
  const navigate = useNavigate();
  const cart = useCart();
  const categories = useCategories();

  const [categoryId, setCategoryId] = useState<string | undefined>(undefined);
  const [searchInput, setSearchInput] = useState("");
  const debouncedSearch = useDebouncedValue(searchInput, SEARCH_DEBOUNCE_MS);
  const activeSearch = debouncedSearch.trim().length >= SEARCH_MIN_LENGTH ? debouncedSearch.trim() : "";

  const totalQty = cart.data?.items.reduce((sum, item) => sum + item.qty, 0) ?? 0;

  useEffect(() => {
    if (totalQty === 0 || !cart.data) {
      mainButton.hide();
      return;
    }
    mainButton.setText(`${messages.cartLabel} · ${cart.data.items_total_text}`);
    mainButton.show();
    return mainButton.onClick(() => navigate("cart"));
  }, [totalQty, cart.data, navigate]);

  return (
    <div className="min-h-screen bg-bg pb-6">
      <header className="flex items-start justify-between gap-2 p-4">
        <div>
          <h1 className="text-lg font-semibold text-text">{shop.title}</h1>
          {shop.description ? <p className="text-sm text-hint">{shop.description}</p> : null}
        </div>
        <div className="flex shrink-0 items-center gap-3">
          <button
            type="button"
            onClick={() => navigate("orders")}
            className="flex items-center gap-1 text-sm text-hint"
          >
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <circle cx="12" cy="12" r="9" />
              <path d="M12 7v5l3 3" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span>{messages.ordersLink}</span>
          </button>
          <button
            type="button"
            aria-label={messages.cartLabel}
            onClick={() => navigate("cart")}
            className="relative text-text"
          >
            <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M4 5h2l2.2 9.5a1 1 0 0 0 1 .8h7.6a1 1 0 0 0 1-.8L19 8H7" strokeLinecap="round" strokeLinejoin="round" />
              <circle cx="10" cy="19" r="1.4" />
              <circle cx="17" cy="19" r="1.4" />
            </svg>
            {totalQty > 0 ? (
              <span
                data-testid="cart-badge"
                className="absolute -right-2 -top-1 min-w-5 rounded-full bg-accent px-1 text-center text-xs leading-5 text-button-text"
              >
                {totalQty}
              </span>
            ) : null}
          </button>
        </div>
      </header>

      {shop.search_enabled ? (
        <div className="pb-3">
          <SearchBar value={searchInput} onChange={setSearchInput} />
        </div>
      ) : null}

      {categories.data && categories.data.items.length > 0 ? (
        <CategoryChips categories={categories.data.items} activeCategoryId={categoryId} onSelect={setCategoryId} />
      ) : null}

      <ProductGrid
        categoryId={categoryId}
        search={activeSearch}
        managerUrl={shop.manager_url}
        onOpenProduct={(productId) => navigate(`product/${productId}`)}
        onClearSearch={() => setSearchInput("")}
      />
    </div>
  );
}
